import { BellIcon as BellIconOutline } from '@heroicons/react/24/outline';
import { AtSign, Bookmark, MessageSquare } from 'lucide-react';

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import type { UnreadCountsSchema } from '@/services/api/notificationsService';

export type NotificationNavTab =
  | 'messages'
  | 'mentions'
  | 'updates';

interface NotificationNavProps {
  activeTab: NotificationNavTab;
  onTabChange: (tab: NotificationNavTab) => void;
  unreadCounts: UnreadCountsSchema;
}

interface NavItemProps {
  icon: React.ReactNode;
  label: string;
  badge?: number;
  isActive: boolean;
  onClick: () => void;
  disabled?: boolean;
  disabledTooltip?: string;
}

const NavItem: React.FC<NavItemProps> = ({
  icon,
  label,
  badge,
  isActive,
  onClick,
  disabled,
  disabledTooltip,
}) => {
  const button = (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      className={cn(
        'relative flex flex-col items-center justify-center w-full py-2.5 transition-colors',
        disabled ? 'cursor-not-allowed' : 'hover:opacity-80',
      )}
      aria-label={label}
    >
      {/* Icon container with highlight */}
      <div
        className={cn(
          'relative flex items-center justify-center w-10 h-10 rounded-xl transition-colors',
          isActive && 'bg-indigo-100 text-indigo-600',
          !isActive && disabled && 'text-gray-400',
          !isActive && !disabled && 'text-gray-500 hover:bg-gray-100',
        )}
      >
        {icon}
        {badge !== undefined && badge > 0 && (
          <span className="absolute -top-1 -right-1 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white bg-indigo-600 rounded-full">
            {badge > 99 ? '99+' : badge}
          </span>
        )}
      </div>
      <span
        className={cn(
          'mt-1 text-[9px] font-bold',
          isActive && 'text-indigo-600',
          !isActive && disabled && 'text-gray-400',
          !isActive && !disabled && 'text-gray-500',
        )}
      >
        {label}
      </span>
    </button>
  );

  if (disabled && disabledTooltip) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{button}</TooltipTrigger>
        <TooltipContent side="right">{disabledTooltip}</TooltipContent>
      </Tooltip>
    );
  }

  return button;
};

export const NotificationNav: React.FC<NotificationNavProps> = ({
  activeTab,
  onTabChange,
  unreadCounts,
}) => {
  return (
    <nav className="flex flex-col w-16 bg-white border-r border-gray-200 flex-shrink-0 pt-3">
      {/* Messages tab - client SMS (email coming soon) */}
      <Tooltip>
        <TooltipTrigger asChild>
          <div>
            <NavItem
              icon={<MessageSquare className="w-5 h-5" />}
              label="Messages"
              badge={unreadCounts.messages}
              isActive={activeTab === 'messages'}
              onClick={() => onTabChange('messages')}
            />
          </div>
        </TooltipTrigger>
        <TooltipContent side="right" className="text-xs">
          <p className="font-medium">Client SMS</p>
          <p className="text-gray-400">Email coming soon</p>
        </TooltipContent>
      </Tooltip>

      {/* Mentions tab */}
      <NavItem
        icon={<AtSign className="w-5 h-5" />}
        label="Mentions"
        badge={unreadCounts.mentions}
        isActive={activeTab === 'mentions'}
        onClick={() => onTabChange('mentions')}
      />

      {/* Updates tab */}
      <NavItem
        icon={<BellIconOutline className="w-5 h-5" />}
        label="Updates"
        badge={unreadCounts.updates}
        isActive={activeTab === 'updates'}
        onClick={() => onTabChange('updates')}
      />

      {/* V2: Later/Saved tab - disabled */}
      <NavItem
        icon={<Bookmark className="w-5 h-5" />}
        label="Later"
        isActive={false}
        onClick={() => {}}
        disabled
        disabledTooltip="Coming soon"
      />
    </nav>
  );
};
